'use client';

import Image from 'next/image';
import { type MessageKeys, useLocale, useTranslations } from 'next-intl';
import React, { useEffect } from 'react';
import { Button } from '@/app/components/ui/button';
import { HOME_PAGE, SITE } from '@/app/constants';
import { cn, reportEvent } from '@/app/utils';
import { useRouter } from '@/i18n/routing';
import { FeatureCarousel, type FeatureCarouselItem } from '../../_components/FeatureCarousel';
import ProductInputTool from '../../_components/ProductInputTool';
import { reportProductPageElementClick } from '../../_components/ProductInputTool/utils';
import { QuestionSection } from './QuestionSection';

const HERO_MEDIA_ORIGIN = SITE === 'china' ? 'https://image01-aliyun.vidu.zone' : 'https://image01.cf.vidu.studio';

const HERO_MEDIA = {
  videoSrc: `${HERO_MEDIA_ORIGIN}/vidu/media-asset/Q4-0110e797.mp4`,
  videoPoster: `${HERO_MEDIA_ORIGIN}/vidu/media-asset/Q4-c510a4f0.webp`,
};
const HERO_TITLE_IMAGES = {
  zh: `${HERO_MEDIA_ORIGIN}/vidu/media-asset/q4dialog-title-zh-preview2-4965b48f.webp`,
  en: `${HERO_MEDIA_ORIGIN}/vidu/media-asset/q4dialog-title-en-preview2-b7a7eb46.webp`,
} as const;

const SECTION_MEDIA = {
  reference: {
    videoSrc:
      SITE === 'china'
        ? 'https://image01-aliyun.vidu.zone/vidu/media-asset/2-74ad9b79.mp4'
        : 'https://image01.cf.vidu.studio/vidu/media-asset/2-74ad9b79.mp4',
    videoPoster:
      SITE === 'china'
        ? 'https://image01-aliyun.vidu.zone/vidu/media-asset/2-a4922d81.webp'
        : 'https://image01.cf.vidu.studio/vidu/media-asset/2-caab577d.webp',
  },
  image: {
    videoSrc:
      SITE === 'china'
        ? 'https://image01-aliyun.vidu.zone/vidu/media-asset/3-3ad837f2.mp4'
        : 'https://image01.cf.vidu.studio/vidu/media-asset/3-3ad837f2.mp4',
    videoPoster:
      SITE === 'china'
        ? 'https://image01-aliyun.vidu.zone/vidu/media-asset/3-09ac216f.webp'
        : 'https://image01.cf.vidu.studio/vidu/media-asset/3-a7f63e3b.webp',
  },
} as const;

export const ViduQ4: React.FC = () => {
  const router = useRouter();
  const locale = useLocale();
  const t = useTranslations('vidu-q4');

  // Hero Section 数据
  const heroFeature = {
    title: t('title'),
    description: t('subtitle'),
    ...HERO_MEDIA,
  };

  const buildSpecs = (
    specKeys: [[string, string], [string, string], [string, string]],
    media: (typeof SECTION_MEDIA)[keyof typeof SECTION_MEDIA],
  ): FeatureCarouselItem[] =>
    specKeys.map(([titleKey, descriptionKey]) => ({
      id: titleKey,
      title: t(titleKey as Parameters<typeof t>[0]),
      description: t(descriptionKey as Parameters<typeof t>[0]),
      ...media,
    }));

  // 第二屏：参考生视频；第三屏：图生视频（左图右字由 reverseLayout 控制）
  const sections = [
    {
      id: 'screen2-reference',
      title: t('screen2.title'),
      description: t('screen2.description'),
      features: buildSpecs(
        [
          ['screen2.specA', 'screen2.specADescription'],
          ['screen2.specB', 'screen2.specBDescription'],
          ['screen2.specC', 'screen2.specCDescription'],
        ],
        SECTION_MEDIA.reference,
      ),
      onButtonClick: () => handleSectionGenerate('reference to video', '/create/character2video'),
    },
    {
      id: 'screen3-image',
      title: t('screen3.title'),
      description: t('screen3.description'),
      features: buildSpecs(
        [
          ['screen3.specA', 'screen3.specADescription'],
          ['screen3.specB', 'screen3.specBDescription'],
          ['screen3.specC', 'screen3.specCDescription'],
        ],
        SECTION_MEDIA.image,
      ),
      onButtonClick: () => handleSectionGenerate('image to video', '/create/img2video'),
    },
  ];

  useEffect(() => {
    reportEvent('feature_page_view', {
      from_page: 'vidu q4',
    });
  }, []);

  // 处理生成按钮点击
  const handleGenerateClick = () => {
    reportEvent('element_click', {
      element_name: 'generate-now',
      page: 'vidu-q4',
    });
    reportProductPageElementClick('generate now');
    router.push(HOME_PAGE);
  };

  // 特性区按钮：按屏跳转对应创作入口（参考生 / 图生）
  const handleSectionGenerate = (elementName: string, route: string) => {
    reportEvent('element_click', {
      element_name: 'generate-now',
      page: 'vidu-q4',
    });
    reportProductPageElementClick(elementName);
    router.push(route);
  };

  // Highlights 优势项配置：大数字替代 icon，数字沿用 icon 的浅蓝渐变
  const highlights: {
    id: string;
    metric: string;
    labelKey: MessageKeys<
      IntlMessages,
      'highlights.resolution' | 'highlights.videoLength' | 'highlights.refImages' | 'highlights.refAudio'
    >;
  }[] = [
    { id: 'resolution', metric: '4K', labelKey: 'highlights.resolution' },
    { id: 'videoLength', metric: '16S', labelKey: 'highlights.videoLength' },
    { id: 'refImages', metric: '15', labelKey: 'highlights.refImages' },
    { id: 'refAudio', metric: '3', labelKey: 'highlights.refAudio' },
  ];

  return (
    // 将页面内部层级限制在 header 下方，同时保留 Hero 对后续视频的覆盖。
    <div className="isolate flex min-h-screen w-full flex-col bg-black text-white">
      {/* Hero Section - 全屏视频 */}
      <section className="relative z-20 h-screen w-full overflow-visible">
        <video
          className="absolute inset-0 size-full object-cover"
          src={heroFeature.videoSrc}
          poster={heroFeature.videoPoster}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
        />
        {/* 暗色遮罩，保证文字与输入框可读 */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/20 to-black/70" />
        {/* 底部蒙版：由透明渐变到纯黑，与第二屏 bg-black 无缝接壤 */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[240px] bg-gradient-to-b from-transparent to-black sm:h-[160px]" />

        <div className="relative z-10 flex h-full flex-col items-center px-4 text-center">
          <div className="-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2 w-full px-4 text-center sm:top-24 sm:translate-y-0">
            <img
              src={HERO_TITLE_IMAGES[locale === 'zh' ? 'zh' : 'en']}
              alt={heroFeature.title}
              className="mx-auto h-auto w-[360px] max-w-full sm:w-[240px]"
            />
          </div>
          {/* 输入框悬浮在视频上，靠底部 */}
          <div className="relative z-20 mt-auto mb-20 w-full max-w-[720px] sm:mb-10 md:max-w-full lg:max-w-[680px]">
            <ProductInputTool />
          </div>
        </div>
      </section>

      {/* Feature Sections */}
      {sections.map((section, index) => (
        <FeatureCarousel
          className="pt-40"
          key={section.id}
          title={section.title}
          description={section.description}
          descriptionClassName="text-white/40"
          titleClassName="w-[58vw] pb-8"
          features={section.features}
          buttonText={t('buttons.generateNow')}
          onButtonClick={section.onButtonClick}
          reverseLayout={index % 2 === 1}
        />
      ))}

      {/* Highlights Section */}
      <section className="w-full bg-black pt-28 sm:pt-6">
        <div className="mx-auto max-w-[1440px] px-8 py-12 text-center md:px-4 md:py-8">
          <div className="mb-16 text-center">
            <h2 className="font-semibold text-[40px] leading-tight sm:text-[36px]">{t('highlights.title')}</h2>
          </div>
          <div className="flex justify-between sm:flex-wrap sm:justify-center sm:gap-y-8">
            {highlights.map((highlight, index) => {
              return (
                <React.Fragment key={highlight.id}>
                  <div className="flex w-[20%] flex-col items-center text-center sm:w-[48%]">
                    <div className="relative mb-4 aspect-square w-[120px] sm:mb-2 sm:w-[88px]">
                      {/* iOS 图标式羊角曲线（超椭圆）描边，渐变与数字一致，40% 透明度 */}
                      <svg
                        className="absolute inset-0 size-full"
                        viewBox="0 0 100 100"
                        fill="none"
                        aria-hidden="true"
                        preserveAspectRatio="none"
                      >
                        <defs>
                          <linearGradient
                            id={`sq-${highlight.id}`}
                            x1="50"
                            y1="1"
                            x2="50"
                            y2="99"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop stopColor="#78A7FE" />
                            <stop offset="1" stopColor="#BFD0FF" />
                          </linearGradient>
                        </defs>
                        <path
                          d="M28 1 L72 1 C86 1 99 14 99 28 L99 72 C99 86 86 99 72 99 L28 99 C14 99 1 86 1 72 L1 28 C1 14 14 1 28 1 Z"
                          stroke={`url(#sq-${highlight.id})`}
                          strokeWidth="2"
                          strokeOpacity="0.4"
                        />
                      </svg>
                      <div className="flex size-full items-center justify-center">
                        <span className="bg-gradient-to-b from-[#78A7FE] to-[#BFD0FF] bg-clip-text font-semibold text-[56px] text-transparent leading-none sm:text-[40px]">
                          {highlight.metric}
                        </span>
                      </div>
                    </div>
                    <div className="mx-auto min-w-[110px] whitespace-pre-line text-center font-medium text-base text-white leading-5 sm:text-xs">
                      {t(highlight.labelKey)}
                    </div>
                  </div>

                  {index < highlights.length - 1 && (
                    <div className={cn('flex items-center', index === 1 && 'sm:hidden')}>
                      <div className="h-[60px] w-[2px] bg-white/20" />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <QuestionSection />

      {/* CTA Banner */}
      <section className="relative mb-[160px] h-[364px] w-full sm:h-[219px]">
        <Image
          src="https://image01.cf.vidu.studio/vidu/media-asset/CTA_Banner-0d6559ab.webp"
          alt="CTA Banner"
          fill
          className="object-cover"
          sizes="100vw"
          loading="lazy"
        />

        {/* 内容覆盖层 */}
        <div className="absolute inset-0 z-10 flex items-center justify-center">
          <div className="mx-auto max-w-4xl px-4 text-center sm:px-8">
            <h2 className="mb-10 font-bold text-[48px] sm:font-semibold sm:text-[32px]">{t('cta.title')}</h2>
            <Button
              className="h-[60px] min-w-[224px] rounded-[100px] bg-[#0086FF] px-10 py-5 font-medium text-white text-xl shadow-lg transition-all hover:shadow-xl sm:h-12 sm:w-[195px] sm:py-4 sm:text-base"
              onClick={handleGenerateClick}
            >
              {t('buttons.generateNow')}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
